import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Download, Copy, Check, CircleNotch, ArrowSquareOut,
  InstagramLogo, TiktokLogo, YoutubeLogo, LinkedinLogo, TwitterLogo, ChatCircleDots,
} from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";
import { downloadFromUrl } from "@/components/shared";

/**
 * One-click publish helper for any finished video.
 * • Big Download MP4 button (keeps things simple)
 * • Platform picker (IG / TikTok / YT Shorts / LinkedIn / X / Threads)
 * • AI-generated platform-tuned caption + hashtags via /api/publish/caption
 * • Copy caption / Copy hashtags / Copy both
 * • Deep-link button that opens the platform's upload page
 *
 * Props: { videoUrl, script, topic?, filename?, compact? }
 */
const PLATFORM_ICONS = {
  instagram: InstagramLogo,
  tiktok: TiktokLogo,
  youtube_shorts: YoutubeLogo,
  linkedin: LinkedinLogo,
  twitter: TwitterLogo,
  threads: ChatCircleDots,
};

const PLATFORM_COLORS = {
  instagram: "from-pink-500 to-orange-400",
  tiktok: "from-black to-cyan-500",
  youtube_shorts: "from-red-600 to-red-400",
  linkedin: "from-[#0A66C2] to-[#0A66C2]",
  twitter: "from-black to-zinc-700",
  threads: "from-zinc-900 to-zinc-700",
};

export default function PublishPanel({ videoUrl, script, topic, filename = "video.mp4", compact = false }) {
  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected] = useState("instagram");
  const [captionData, setCaptionData] = useState(null); // { caption, hashtags, deep_link, ... }
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null); // "caption" | "hashtags" | "both"
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/publish/platforms`, { headers: getAuthHeaders() });
        setPlatforms(r.data.platforms || []);
      } catch { /* silent */ }
    })();
  }, []);

  const genCaption = async (platform) => {
    if (!script) return;
    setLoading(true);
    setError(null);
    setCaptionData(null);
    try {
      const r = await axios.post(
        `${API}/publish/caption`,
        { script, platform, topic },
        { headers: getAuthHeaders() },
      );
      setCaptionData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Caption generation failed. Try again in a sec.");
    } finally {
      setLoading(false);
    }
  };

  const doCopy = async (kind, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch { setError("Clipboard blocked — please select + copy manually."); }
  };

  const fullText = captionData ? `${captionData.caption}\n\n${captionData.hashtags.join(" ")}` : "";
  const selectedPlatform = platforms.find((p) => p.id === selected);

  return (
    <div className={`rounded-2xl border-2 border-[#E91E8C]/20 bg-white p-4 sm:p-5 space-y-4 ${compact ? "" : ""}`} data-testid="publish-panel">
      <div>
        <p className="text-[10px] uppercase font-bold tracking-widest text-[#E91E8C] mb-1">🚀 Ready to post?</p>
        <h3 className="text-lg font-bold text-zinc-900">Download and publish anywhere</h3>
        <p className="text-xs text-zinc-500 mt-0.5">Pick a platform → we&apos;ll write a caption tuned for it → copy &amp; paste.</p>
      </div>

      {/* Download row */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => downloadFromUrl(videoUrl, filename)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-700"
          data-testid="publish-download-btn"
        >
          <Download size={14} weight="bold" /> Download MP4
        </button>
        <a
          href={videoUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-zinc-600 hover:text-[#E91E8C] font-semibold"
        >
          <ArrowSquareOut size={12} /> Open in new tab
        </a>
      </div>

      {/* Platform picker */}
      <div>
        <p className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider mb-2">1. Pick your platform</p>
        <div className="flex flex-wrap gap-1.5">
          {platforms.map((p) => {
            const Icon = PLATFORM_ICONS[p.id] || InstagramLogo;
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                onClick={() => { setSelected(p.id); setCaptionData(null); setError(null); }}
                data-testid={`publish-platform-${p.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                  active
                    ? `text-white bg-gradient-to-r ${PLATFORM_COLORS[p.id]}`
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                <Icon size={13} weight={active ? "fill" : "regular"} />
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Generate + caption view */}
      <div>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <p className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">
            2. Get your caption {selectedPlatform && <span className="text-zinc-400 normal-case">· {selectedPlatform.char_limit} char limit</span>}
          </p>
          <button
            onClick={() => genCaption(selected)}
            disabled={loading || !script}
            data-testid="publish-generate-caption-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8C] text-white text-xs font-semibold hover:bg-[#c91876] disabled:opacity-50"
          >
            {loading ? <><CircleNotch size={12} className="animate-spin" /> Writing…</> : (captionData ? "Regenerate" : "Generate caption")}
          </button>
        </div>

        {error && <p className="text-xs text-red-600 mb-2" data-testid="publish-error">{error}</p>}

        {captionData && (
          <div className="space-y-2" data-testid="publish-caption-block">
            <textarea
              readOnly
              value={fullText}
              rows={compact ? 5 : 8}
              onFocus={(e) => e.currentTarget.select()}
              data-testid="publish-caption-textarea"
              className="w-full p-3 rounded-lg border border-zinc-200 text-sm text-zinc-800 leading-relaxed font-mono bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 resize-none"
            />
            <p className={`text-[10px] ${captionData.char_count > captionData.char_limit ? "text-red-600 font-bold" : "text-zinc-400"}`}>
              {captionData.char_count} / {captionData.char_limit} chars
              {captionData.char_count > captionData.char_limit && " · trim to fit"}
            </p>

            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => doCopy("both", fullText)}
                data-testid="publish-copy-both-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8C] text-white text-xs font-semibold hover:bg-[#c91876]"
              >
                {copied === "both" ? <><Check size={12} weight="bold" /> Copied!</> : <><Copy size={12} /> Copy caption + tags</>}
              </button>
              <button
                onClick={() => doCopy("caption", captionData.caption)}
                data-testid="publish-copy-caption-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-700 text-xs font-semibold hover:bg-zinc-200"
              >
                {copied === "caption" ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                Copy caption only
              </button>
              <button
                onClick={() => doCopy("hashtags", captionData.hashtags.join(" "))}
                data-testid="publish-copy-hashtags-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-700 text-xs font-semibold hover:bg-zinc-200"
              >
                {copied === "hashtags" ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                Hashtags only
              </button>
              <a
                href={captionData.deep_link}
                target="_blank"
                rel="noreferrer"
                data-testid="publish-deeplink-btn"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-gradient-to-r ${PLATFORM_COLORS[selected]} hover:opacity-90`}
              >
                Open {selectedPlatform?.label} <ArrowSquareOut size={12} />
              </a>
            </div>

            <p className="text-[10px] text-zinc-400 pt-1">
              💡 Tip: after clicking &quot;Open&quot;, paste your caption and pick the downloaded MP4. Direct-publish (one-click to IG/TikTok) is coming soon.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
